import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "feather-icons-react/build/IconComponents";
import { signIn, checkAuth } from "../../firebase/authService";
import Preloader from "../../Components/Preloader";

const Login = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [pageBackground, setPageBackground] = useState({
    title: 'Login'
  });

  useEffect(() => {
    if (!localStorage.getItem("reloaded")) {
      localStorage.setItem("reloaded", "true");
      window.location.reload();
    } else {
      localStorage.removeItem("reloaded");
      const checkLoggedIn = async () => {
        try {
          const isLoggedIn = await checkAuth();
          if (isLoggedIn) {
            navigate("/admin-dashboard");
          } else {
            setIsLoading(false);
          }
        } catch (error) {
          console.error("Error checking authentication:", error);
          setIsLoading(false);
        }
      };

      checkLoggedIn();
    }

  }, [navigate]);


  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    try {
      await signIn(email, password);
      navigate("/admin-dashboard");
    } catch (error) {
      console.error("Login failed:", error);
      if (error.code === "auth/invalid-email" || error.code === "auth/wrong-password") {
        setError("Invalid email or password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many login attempts. Please wait a few minutes and try again.");
      } else {
        setError("Failed to login. Please try again later.");
      }
    }
  };

  if (isLoading) {
    return <Preloader />;
  }

  return (
    <>
      <style>{`
        .modern-login-wrapper input[type="email"],
        .modern-login-wrapper input[type="password"],
        .modern-login-wrapper input[type="text"] {
          color: #1a1a2e !important;
          -webkit-text-fill-color: #1a1a2e !important;
          background-color: #ffffff !important;
          caret-color: #1a1a2e !important;
        }
        .modern-login-wrapper input::placeholder {
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
          opacity: 1 !important;
        }
        .modern-login-wrapper label {
          color: #000000 !important;
          -webkit-text-fill-color: #000000 !important;
          background: none !important;
        }
        .modern-login-wrapper input:-webkit-autofill,
        .modern-login-wrapper input:-webkit-autofill:hover,
        .modern-login-wrapper input:-webkit-autofill:focus {
          -webkit-text-fill-color: #1a1a2e !important;
          -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
          box-shadow: 0 0 0px 1000px #ffffff inset !important;
          color: #1a1a2e !important;
        }
      `}</style>
    <div
      className="modern-login-wrapper"
      style={{
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative'
      }}
    >
      {/* Background overlay */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(2px)'
        }}
      />
      
      {/* Login form container */}
      <div 
        className="modern-login-container"
        style={{
          position: 'relative',
          zIndex: 10,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: '20px',
          padding: '40px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          width: '100%',
          maxWidth: '450px',
          margin: '20px'
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <Link to="/">
            <img
              width={200}
              height={60}
              src="/assets/img/webp/logo02.png"
              alt="logo"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </Link>
        </div>

        {/* Title */}
        <h2 
          style={{
            textAlign: 'center',
            marginBottom: '30px',
            color: '#2d3748',
            fontSize: '28px',
            fontWeight: '600'
          }}
        >
          {pageBackground.title}
        </h2>

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label 
              style={{
                display: 'block',
                marginBottom: '8px',
                color: '#4a5568',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Email <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <input
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e2e8f0',
                borderRadius: '10px',
                fontSize: '16px',
                transition: 'all 0.3s ease',
                backgroundColor: '#ffffff',
                color: '#1a1a2e',
                boxSizing: 'border-box'
              }}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label 
              style={{
                display: 'block',
                marginBottom: '8px',
                color: '#4a5568',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Password <span style={{ color: '#e53e3e' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  paddingRight: '50px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '10px',
                  fontSize: '16px',
                  transition: 'all 0.3s ease',
                  backgroundColor: '#ffffff',
                  color: '#1a1a2e',
                  boxSizing: 'border-box'
                }}
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#718096',
                  padding: '4px'
                }}
              >
                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && (
            <div 
              style={{
                backgroundColor: '#fed7d7',
                border: '1px solid #fc8181',
                color: '#c53030',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '20px',
                fontSize: '14px'
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#4a5568' }}>
              <input
                type="checkbox"
                id="remember-me"
                style={{ marginRight: '8px' }}
              />
              Remember me
            </label>
            <Link 
              to="/forgotpassword"
              style={{
                color: '#667eea',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a67d8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#667eea'}
          >
            Login
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default Login;
